"use client";

import { useTranslations } from "next-intl";
import { CreatePasswordStep } from "@/features/auth/components/steps/create-password-step";
import { EmailStep } from "@/features/auth/components/steps/email-step";
import { LinkedAccountStep } from "@/features/auth/components/steps/linked-account-step";
import { PasswordStep } from "@/features/auth/components/steps/password-step";
import { ResetPasswordStep } from "@/features/auth/components/steps/reset-password-step";
import { VerifyCodeStep } from "@/features/auth/components/steps/verify-code-step";
import type { useAuthFlow } from "@/features/auth/hooks/use-auth-flow";
import { AUTH_FORM_IDS } from "@/features/auth/lib/auth-step-config";
import { cn } from "@/lib/utils";

export type AuthFlowController = ReturnType<typeof useAuthFlow>;

type AuthStepsProps = {
  flow: AuthFlowController;
  /** The dialog carries the step title in its header, the page inline. */
  showEmailHeading?: boolean;
  /** Moves the step submit to a host-rendered bottom bar. */
  submitInFooter?: boolean;
  className?: string;
};

export const AuthSteps = ({
  flow,
  showEmailHeading = true,
  submitInFooter = false,
  className,
}: AuthStepsProps) => {
  const t = useTranslations();

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {flow.globalError && (
        <p className="text-sm text-text-danger leading-[22px]" role="alert">
          {t(flow.globalError)}
        </p>
      )}

      {flow.step === "email" && (
        <EmailStep
          defaultEmail={flow.email}
          isSubmitting={flow.isSubmitting}
          onProviderSelect={flow.signInWithProvider}
          onSubmit={flow.submitEmail}
          showHeading={showEmailHeading}
        />
      )}

      {flow.step === "password" && (
        <PasswordStep
          email={flow.email}
          isSubmitting={flow.isSubmitting}
          onChangeEmail={flow.resetToEmailStep}
          onForgotPassword={flow.startPasswordReset}
          onProviderSelect={flow.signInWithProvider}
          onSubmit={flow.submitPassword}
          provider={flow.provider}
          serverError={flow.passwordError}
          submitInFooter={submitInFooter}
          variant={flow.passwordStepVariant}
        />
      )}

      {flow.step === "linked-account" && flow.provider && (
        <LinkedAccountStep
          email={flow.email}
          isSubmitting={flow.isSubmitting}
          onBack={flow.resetToEmailStep}
          onProviderSelect={flow.signInWithProvider}
          provider={flow.provider}
        />
      )}

      {flow.step === "create-password" && (
        <CreatePasswordStep
          email={flow.email}
          isSubmitting={flow.isSubmitting}
          onChangeEmail={flow.resetToEmailStep}
          onSubmit={flow.submitRegistration}
          serverError={flow.newPasswordError}
          submitInFooter={submitInFooter}
        />
      )}

      {flow.step === "verify-code" && (
        <VerifyCodeStep
          email={flow.email}
          formId={AUTH_FORM_IDS["verify-code"]}
          isSubmitting={flow.isSubmitting}
          onResend={flow.resendVerificationCode}
          onSubmit={flow.submitVerificationCode}
          resendSecondsLeft={flow.resendSecondsLeft}
          serverError={flow.codeError}
          submitInFooter={submitInFooter}
        />
      )}

      {flow.step === "reset-code" && (
        <VerifyCodeStep
          email={flow.email}
          formId={AUTH_FORM_IDS["reset-code"]}
          isSubmitting={flow.isSubmitting}
          onResend={flow.resendResetCode}
          onSubmit={flow.submitResetCode}
          resendSecondsLeft={flow.resetResendSecondsLeft}
          serverError={flow.resetCodeError}
          submitInFooter={submitInFooter}
        />
      )}

      {flow.step === "reset-password" && (
        <ResetPasswordStep
          email={flow.email}
          isSubmitting={flow.isSubmitting}
          onSubmit={flow.submitNewPassword}
          serverError={flow.resetPasswordError}
          submitInFooter={submitInFooter}
        />
      )}
    </div>
  );
};
